// src/constants/attendeeStates.js
// Centralización de todos los estados de asistentes y funciones relacionadas

import i18n from '../i18n';

// Estados válidos según el backend (Event.js attendeeSchema)
export const ATTENDEE_STATES = {
  CONFIRMATION_PENDING: 'confirmation_pending',
  ATTENDING: 'attending',
  NOT_ATTENDING: 'not_attending',
  REJECTED_BY_ORGANIZER: 'rejected_by_organizer',
  CANCELLED_BY_USER: 'cancelled_by_user',
  CANCELLED_BY_ORGANIZER: 'cancelled_by_organizer',
  CANCELLATION_PENDING: 'cancellation_pending',
  REFUND_ACCEPTED: 'refund_accepted',
  REFUNDED: 'refunded'
};

// STATE_LABELS eliminado - ahora se usan solo las traducciones i18n

// Orden de prioridad para mostrar estados (mayor número = mayor prioridad)
export const STATE_PRIORITY = {
  [ATTENDEE_STATES.ATTENDING]: 9,
  [ATTENDEE_STATES.CONFIRMATION_PENDING]: 8,
  [ATTENDEE_STATES.CANCELLATION_PENDING]: 7,
  [ATTENDEE_STATES.REFUND_ACCEPTED]: 6,
  [ATTENDEE_STATES.REFUNDED]: 5,
  [ATTENDEE_STATES.NOT_ATTENDING]: 4,
  [ATTENDEE_STATES.CANCELLED_BY_USER]: 3,
  [ATTENDEE_STATES.CANCELLED_BY_ORGANIZER]: 2,
  [ATTENDEE_STATES.REJECTED_BY_ORGANIZER]: 1
};

// Estados que cuentan para la capacidad del evento
export const ACTIVE_STATES = [
  ATTENDEE_STATES.CONFIRMATION_PENDING,
  ATTENDEE_STATES.ATTENDING
];

// Estados de cancelación
export const CANCELLED_STATES = [
  ATTENDEE_STATES.CANCELLED_BY_USER,
  ATTENDEE_STATES.CANCELLED_BY_ORGANIZER,
  ATTENDEE_STATES.REJECTED_BY_ORGANIZER,
  ATTENDEE_STATES.NOT_ATTENDING
];

// Estados relacionados con reembolsos
export const REFUND_STATES = [
  ATTENDEE_STATES.CANCELLATION_PENDING,
  ATTENDEE_STATES.REFUND_ACCEPTED,
  ATTENDEE_STATES.REFUNDED
];

// Funciones helper

/**
 * Obtiene la etiqueta localizada para un estado (sin i18n - para compatibilidad)
 * @param {string} state - El estado del asistente
 * @returns {string} La etiqueta localizada
 */
export const getStateLabel = (state) => {
  if (!state) return 'Estado desconocido';
  return i18n.t(`attendeeStates:states.${state}`, state);
};

/**
 * Función para obtener la etiqueta traducida de un estado
 * @param {string} state - El estado del asistente
 * @returns {string} La etiqueta traducida
 */
export const getTranslatedStateLabel = (state) => {
  if (!state) return i18n.t('attendeeStates:states.unknownStatus', 'Estado desconocido');
  return i18n.t(`attendeeStates:states.${state}`, 'Estado desconocido');
};

/**
 * Función para obtener el mensaje traducido de un estado
 * @param {string} state - El estado del asistente
 * @returns {string} El mensaje traducido
 */
export const getTranslatedStateMessage = (state) => {
  if (!state) return i18n.t('attendeeStates:messages.unknownStatus', 'Estado desconocido.');
  return i18n.t(`attendeeStates:messages.${state}`, '');
};

/**
 * Determina si un estado cuenta para la capacidad del evento
 * @param {string} state - El estado del asistente
 * @returns {boolean} True si el estado es activo
 */
export const isActiveState = (state) => {
  return ACTIVE_STATES.includes(state);
};

/**
 * Determina si un estado es de cancelación
 * @param {string} state - El estado del asistente
 * @returns {boolean} True si el estado es de cancelación
 */
export const isCancelledState = (state) => {
  return CANCELLED_STATES.includes(state);
};

/**
 * Determina si un estado está relacionado con reembolsos
 * @param {string} state - El estado del asistente
 * @returns {boolean} True si el estado es de reembolso
 */
export const isRefundState = (state) => {
  return REFUND_STATES.includes(state);
};

/**
 * Obtiene la prioridad de un estado para ordenamiento
 * @param {string} state - El estado del asistente
 * @returns {number} La prioridad del estado
 */
export const getStatePriority = (state) => {
  return STATE_PRIORITY[state] || 0;
};

/**
 * Obtiene todos los estados válidos como array
 * @returns {string[]} Array con todos los estados válidos
 */
export const getAllStates = () => {
  return Object.values(ATTENDEE_STATES);
};

/**
 * Valida si un estado es válido
 * @param {string} state - El estado a validar
 * @returns {boolean} True si el estado es válido
 */
export const isValidState = (state) => {
  return getAllStates().includes(state);
};

/**
 * Hook personalizado para usar traducciones de estados (para componentes React)
 * @returns {object} Objeto con funciones de traducción
 */
export const useAttendeeStateTranslations = () => {
  // Esta función debe ser usada dentro de componentes React
  // que tengan acceso al hook useTranslation
  return {
    getStateLabel: (state) => {
      if (!state) return 'Estado desconocido';
      
      // Usar directamente las etiquetas cortas de los estados
      return i18n.t(`attendeeStates:states.${state}`, 'Estado desconocido');
    },
    getStateMessage: (state) => {
      if (!state) return 'Estado desconocido.';
      
      // Intentar obtener el mensaje como objeto (con title y description)
      const messageKey = `attendeeStates:messages.${state}`;
      const titleKey = `${messageKey}.title`;
      const descriptionKey = `${messageKey}.description`;
      
      // Verificar si existe el título (indica que es un objeto con estructura)
      const title = i18n.t(titleKey, null);
      const description = i18n.t(descriptionKey, null);
      
      if (title && description) {
        return { title, description };
      }
      
      // Si no es un objeto, devolver como string simple
      return i18n.t(messageKey, '');
    }
  };
};